﻿import "../app";
import "@fiqas/utils"
import { IResourceOf, IResourceResolver, IResourceLink, IIdentifiableCollectionPropertyOf, IIdentifiablePagedCollectionPropertyOf, ActionLinkAction } from "../interfaces/iFqs"
import { Person, FiqasEmployee, GetPersonsResult } from "./person"
import { Customer } from "./customer"
import { WebLink } from "./base"
import { CaseFile } from "./file"
import { RecordHistory } from "./recordHistory"

export type Priority = "high" | "medium" | "low" | "_unsupported";
export type Status = "waitingForRelease" | "approved" | "analysis" | "waitingForUat" | "readyForUat" | "delivered" | "rework" | "inProgress" | "closed" | "open" | "parked" | "cancelled" | "explained" | "_unsupported";
export type CaseType = "bug" | "request" | "support";
export type Severity = "level1" | "level2" | "level3" | "level4" | "level99" | "_unsupported";
export type Impact = "low" | "medium" | "high" | "_unsupported";
export type Environment = "dev" | "test" | "uat" | "prod" | "na";
export type FeedbackStatus = 'provided' | 'requested' | 'notRequired' | '_unsupported';
export type CorrespondenceType = 'email' | 'feedbackFromCustomer' | 'taskDescriptionHtml' | 'taskDescription' | 'generalNote' | 'feedbackRequest' | 'feedbackReply' | 'internal' | '_unsupported';
export type CaseOwnership = "serviceDesk" | "billingBureau" | "projectManagement" | "sales" | "_unsupported";
export type CaseCause = "uat" | "billableSupport" | "configBug" | "toBeDetermined" | "custKnowlegdeIssue" | "custData" | "custConfig" | "custVersionControlIssue" | "custRequest" | "incorrectRequirements" | "changedRequirements" | "softwareBug" | "databaseIssue" | "versionControlIssue" | "other" | "usabilityImprovement" | "paymentProcessing" | "userMaintenance" | "dBAActivitice" | "network" | "thirdParty" | "_unsupported";
export type ResponsibleSDEmployee = "stefan" | "dave" | "ilse01" | "ingeborg" | "tbd" | "fintern";

export interface RecentServiceCasesResult extends IResourceOf<RecentServiceCasesResult> {
    items: CompactRecentServiceCase[];
}

export interface ServiceCaseRef {
    number: string,
    id: IResourceLink,
    type: CaseType
    title?: string
}

export interface CompactServiceCase extends IResourceOf<CompactServiceCase> {
    customer: IResourceResolver<Customer>;
    contactPerson: IResourceResolver<Person>;
    start: ISODateTimeString;
    modifiedOn: ISODateTimeString;
    modifiedBy: string;
    title: string;
    caseNumber: string;
    parentCaseNumber: string;
    childCaseNumbers: Array<string>;
    customerDesc: string;
    customerReference: string;
    createdBy: string;
    statusDesc: string;
    status: Status;
    type: CaseType;
    severity: Severity;
    priority: Priority;
    feedbackStatus: FeedbackStatus;
    actionRequired: boolean;
    requestedDeadline?: ISODateString;
    rememberMeDate?: ISODateString;
    rememberMeReason: string;
}

export interface CompactRecentServiceCase extends CompactServiceCase {
    lastOpened: ISODateTimeString;
}

export interface CompactSearchServiceCase extends CompactServiceCase {
    modifiedOn: ISODateTimeString;
}

export interface GeneralLink<T, D, P> {
    action: ActionLinkAction<T, D, P>;
}

export interface StatusLink {
    action: ActionLinkAction<void, void, void>;
}

export interface FeedbackLink {
    action: ActionLinkAction<{ correspondence: CorrespondenceData }, void, void>;
}



export interface ActionLinks {
    [actiongroup: string]: { [key: string]: GeneralLink<any, any, any> }
    statusLinks?: { [key: string]: StatusLink }
    feedbackStatusLinks?: { [key: string]: FeedbackLink }
    generalLinks?: {
        hasChanged?: GeneralLink<boolean, void, void>
        watch?: GeneralLink<void, void, void>
        unwatch?: GeneralLink<void, void, void>
        addWatchers: GeneralLink<void, void, string | string[]>
        getWatchers: GeneralLink<GetPersonsResult, void, void>
        removeWatchers: GeneralLink<void, void, string | string[]>
    }
}

export interface HasActions {
    actionLinks: ActionLinks
}

export interface ServiceCase extends IResourceOf<ServiceCase>, ServiceCaseData, HasActions {
    customer: IResourceResolver<Customer>
    fiqasOwnership: IResourceResolver<FiqasEmployee>
    contactPerson: IResourceResolver<Person>
    files: IIdentifiableCollectionPropertyOf<CaseFile>
    correspondence: IIdentifiablePagedCollectionPropertyOf<Correspondence> & { generalLinks?: GeneralLink<any, any, any>[] }
    history: IIdentifiableCollectionPropertyOf<RecordHistory>
    watcherCount: Number
}

export interface ServiceCaseData {
    start: ISODateTimeString,
    end?: ISODateTimeString,
    title: string,
    description: string,
    customer: IResourceLink,
    fiqasOwnership: IResourceLink,
    contactPerson: WebLink,
    status: Status,
    type?: CaseType,
    priority: Priority,
    internalTaskLink: string,
    caseNumber: string,
    feedbackStatus?: FeedbackStatus,
    parentCaseNumber: string,
    increasedPriority: boolean,
    requestedDeadline?: ISODateString,
    customerReference: string,
    actionRequired?: boolean,
    duration?: number,
    createdOn: ISODateTimeString,
    createdBy: string,
    caseCause?: CaseCause,
    caseOwnership?: CaseOwnership,
    responsibleSDEmployee?: ResponsibleSDEmployee
    budgetIndication?: number,
    approvedHoursMin?: number,
    approvedHoursMax?: number,
    actualHoursSpent: number;
    approvedHoursCommentBySD: string,
    spTsTask: string,
    rememberMeDate?: ISODateString,
    rememberMeReason: string,
    ignoreChatMemoSequence: string;
    ignoreMailing?: boolean;
}

export interface Problem extends ServiceCase, IResourceOf<Problem>, ProblemData, HasActions {
    id: IResourceResolver<Problem>;
    customer: IResourceResolver<Customer>,
    contactPerson: IResourceResolver<Person>,
    fiqasOwnership: IResourceResolver<FiqasEmployee>
}

export interface ProblemData extends ServiceCaseData {
    severity: Severity,
    environment?: Environment,
    systemDown?: boolean,
    impactCategory?: Impact,
    workaroundAvailable: boolean
}

export interface Request extends ServiceCase, IResourceOf<Request>, RequestData, HasActions {
    id: IResourceResolver<Request>;
    customer: IResourceResolver<Customer>,
    contactPerson: IResourceResolver<Person>,
    fiqasOwnership: IResourceResolver<FiqasEmployee>
}

export interface RequestData extends ServiceCaseData { }

export interface Correspondence extends IResourceOf<Correspondence>, CorrespondenceData {
    createdById: IResourceResolver<Person>
    correspondenceType: CorrespondenceType,
    originalContent: string,
}

export interface CorrespondenceData {
    content: string,
    createdOn: ISODateTimeString,
    createdBy: string,
    bySupportEmployee: boolean,
    byInternalCustomer: boolean,
    byCustomer: boolean,
    correspondenceType?: CorrespondenceType
}

export interface SearchInfo { }

export interface SearchData {
    caseStatuses?: Status[];
    feedbackStatuses?: FeedbackStatus[];
    createdOnOrAfter?: ISODateString;
    createdBefore?: ISODateString;
    modifiedOnOrAfter?: ISODateString;
    modifiedBefore?: ISODateString;
    deadlineOnOrAfter?: ISODateString;
    deadlineBefore?: ISODateString;
    rememberOnOrAfter?: ISODateString;
    rememberBefore?: ISODateString;
    caseTypes?: CaseType[];
    correspondenceContent?: string;
    severities?: Severity[];
    priorities?: Priority[];
    environments?: Environment[];
    createdBy?: string;
    createdById?: WebLink;
    watchedById?: WebLink;
    contactPersonId?: WebLink;
    contactPerson?: string;
    caseNumber?: string;
    description?: string;
    title?: string;
    customerRef?: string;
    openCasesOnly?: boolean;
    projectTreeOfNumber?: string;
    includeNeighbour?: boolean;
}
